import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDownWideNarrow, ArrowRight, Bell, Bookmark, BookOpen, ChevronRight, Clock, GraduationCap, Hash, Heart,
  Newspaper, PenLine, Search, X,
} from 'lucide-react';
import { articleService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import {
  Button, EmptyState, GlobalShell, Loader, PageHeader, SectionTitle, Tabs,
} from '../components/ui';
import { ArticleCover, ArticleRow, TagPill } from '../components/articles/ArticleBits';
import { shortDate } from '../utils/articles';
import { initials } from '../utils/share';
import './Articles.css';

const TABS = [
  { value: 'all', label: 'Todos', icon: Newspaper },
  { value: 'following', label: 'Temas que sigo', icon: Bell },
  { value: 'saved', label: 'Salvos', icon: Bookmark },
  { value: 'mine', label: 'Meus artigos', icon: PenLine },
];

const VISIBLE_TAGS = 6;

const EMPTY = {
  all: ['Nenhum artigo por aqui', 'Seja a primeira pessoa a escrever sobre um tema.'],
  following: ['Você ainda não segue nenhum tema', 'Abra um tema e clique em "Seguir tema" para ver os artigos dele aqui.'],
  saved: ['Nenhum artigo salvo', 'Use o marcador ao lado de um artigo para guardar e ler depois.'],
  mine: ['Você ainda não escreveu artigos', 'Compartilhe o que aprendeu com a turma.'],
};

/** Lista global de artigos: abas com busca, filtros numa linha, destaque, feed e lateral por curso. */
function Articles() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courses } = useCourse();
  const [params, setParams] = useSearchParams();
  const tab = params.get('aba') || 'all';
  const tag = params.get('tema') || '';
  const course = params.get('curso') || '';
  const sort = params.get('ordem') || 'recent';
  const q = params.get('q') || '';

  const [search, setSearch] = useState(q);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [featured, setFeatured] = useState(null);
  const [sidebar, setSidebar] = useState(null);
  const [allTags, setAllTags] = useState(false);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    articleService.sidebar().then(setSidebar).catch(() => setSidebar(null));
    articleService.featured().then(setFeatured).catch(() => setFeatured(null));
  }, []);

  useEffect(() => {
    setError(false);
    setData(null);
    setPage(0);
    articleService.list({ tab, tag: tag || undefined, course: course || undefined, sort, q: q || undefined, page: 0 })
      .then(setData)
      .catch(() => setError(true));
  }, [tab, tag, course, sort, q]);

  const loadMore = () => {
    setLoadingMore(true);
    articleService.list({ tab, tag: tag || undefined, course: course || undefined, sort, q: q || undefined, page: page + 1 })
      .then(next => {
        setData(d => ({ ...next, items: [...d.items, ...next.items] }));
        setPage(p => p + 1);
      })
      .finally(() => setLoadingMore(false));
  };

  const open = (a) => navigate(a.status === 'draft' ? `/artigos/${a.id}/editar` : `/artigos/${a.slug}`);

  const tags = sidebar?.popularTags || [];
  const shownTags = allTags ? tags : tags.slice(0, VISIBLE_TAGS);
  const moreTags = Math.max(0, (sidebar?.tagTotal || 0) - VISIBLE_TAGS);
  const showFeatured = featured && tab === 'all' && !tag && !course && !q;
  // O destaque não se repete como primeiro item da lista
  const items = useMemo(
    () => (data?.items || []).filter(a => !(showFeatured && a.id === featured.id)),
    [data, showFeatured, featured],
  );
  const hidden = (data?.items?.length || 0) - items.length;
  const listCount = data ? data.total - hidden : null;
  const sectionLabel = tab === 'all' ? (sort === 'popular' ? 'Mais curtidos' : 'Recentes') : TABS.find(t => t.value === tab).label;
  const counts = data?.counts;
  const courseName = useMemo(() => sidebar?.byCourse?.find(c => c.slug === course)?.name, [sidebar, course]);

  return (
    <GlobalShell>
      <div className="ss-page ss-page--wide arts-page">
        <PageHeader
          icon={Newspaper}
          eyebrow={courseName || 'Todos os temas'}
          title="Artigos"
          subtitle="Textos escritos pela turma, sobre os cursos e além deles."
          actions={!user?.demo && <Button icon={PenLine} onClick={() => navigate('/artigos/novo')} data-tour="escrever">Escrever artigo</Button>}
        />

        <div className="arts-bar" data-tour="artigos-abas">
          <Tabs
            tabs={TABS.map(t => ({ ...t, count: counts?.[t.value] }))}
            value={tab}
            onChange={(v) => setParam('aba', v === 'all' ? '' : v)}
          />
          <form
            className="arts-search"
            role="search"
            onSubmit={(e) => { e.preventDefault(); setParam('q', search.trim()); }}
          >
            <Search size={15} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por título, tema ou autor..."
              aria-label="Buscar artigos"
            />
            {q && (
              <button type="button" className="arts-search__clear" onClick={() => { setSearch(''); setParam('q', ''); }} aria-label="Limpar busca">
                <X size={13} aria-hidden="true" />
              </button>
            )}
          </form>
        </div>

        <div className="arts-filters">
          <div className={`arts-filters__tags ${allTags ? 'is-open' : ''}`}>
            <TagPill tag="Todos" count={counts?.all} active={!tag} onClick={() => setParam('tema', '')} />
            {shownTags.map(t => (
              <TagPill key={t.slug} tag={t.name} count={t.count} active={tag === t.slug} onClick={() => setParam('tema', t.slug)} />
            ))}
            {moreTags > 0 && !allTags && (
              <button type="button" className="arts-filters__more" onClick={() => setAllTags(true)}>+{moreTags}</button>
            )}
          </div>
          <label className="arts-select">
            <GraduationCap size={13} aria-hidden="true" />
            <select value={course} onChange={(e) => setParam('curso', e.target.value)} aria-label="Curso">
              <option value="">Qualquer curso</option>
              {courses.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              <option value="none">Sem curso vinculado</option>
            </select>
          </label>
          <label className="arts-select">
            <ArrowDownWideNarrow size={13} aria-hidden="true" />
            <select value={sort} onChange={(e) => setParam('ordem', e.target.value === 'recent' ? '' : e.target.value)} aria-label="Ordenar">
              <option value="recent">Mais recentes</option>
              <option value="popular">Mais curtidos</option>
            </select>
          </label>
        </div>

        {showFeatured && <Featured article={featured} onOpen={open} />}

        <div className="arts-grid">
          <section className="arts-main">
            <SectionTitle
              icon={sort === 'popular' ? Heart : Clock}
              aside={listCount != null && `${listCount} ${listCount === 1 ? 'artigo' : 'artigos'}`}
            >
              {sectionLabel}
            </SectionTitle>
            {error ? (
              <EmptyState file="artigos.xml" title="Não foi possível carregar os artigos" description="Verifique sua conexão e tente de novo." />
            ) : !data ? (
              <Loader label="Carregando artigos..." />
            ) : items.length === 0 ? (
              <EmptyState
                file="artigos.xml"
                title={q || tag || course ? 'Nenhum artigo com esses filtros' : EMPTY[tab][0]}
                description={q || tag || course ? 'Tente outro tema, curso ou busca.' : EMPTY[tab][1]}
                action={(tab === 'mine' || tab === 'all') && !user?.demo && !q && !tag && !course && (
                  <Button icon={PenLine} onClick={() => navigate('/artigos/novo')}>Escrever artigo</Button>
                )}
              />
            ) : (
              <>
                <div className="arts-feed">
                  {items.map((a, i) => <ArticleRow key={a.id} article={a} onOpen={open} showStatus={tab === 'mine'} tour={i === 0 ? 'artigo-item' : undefined} />)}
                </div>
                {data.hasMore && (
                  <div className="arts-more">
                    <Button variant="outline" onClick={loadMore} disabled={loadingMore}>
                      {loadingMore ? 'Carregando...' : 'Carregar mais'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </section>

          <aside className="arts-side">
            <h2 className="arts-side__title">Por curso</h2>
            <div className="arts-courses">
              {(sidebar?.byCourse || []).map(c => (
                <button
                  key={c.slug}
                  type="button"
                  className={`arts-course ${course === c.slug ? 'is-active' : ''}`}
                  onClick={() => setParam('curso', course === c.slug ? '' : c.slug)}
                  aria-pressed={course === c.slug}
                >
                  <span className="arts-course__icon" style={{ background: c.color || 'var(--brand)' }}><BookOpen size={14} aria-hidden="true" /></span>
                  <span className="arts-course__text">
                    <strong>{c.name}</strong>
                    <span>{c.count} {c.count === 1 ? 'artigo' : 'artigos'}</span>
                  </span>
                  <ChevronRight size={14} aria-hidden="true" />
                </button>
              ))}
              <button
                type="button"
                className={`arts-course ${course === 'none' ? 'is-active' : ''}`}
                onClick={() => setParam('curso', course === 'none' ? '' : 'none')}
                aria-pressed={course === 'none'}
              >
                <span className="arts-course__icon arts-course__icon--none"><Hash size={14} aria-hidden="true" /></span>
                <span className="arts-course__text">
                  <strong>Sem curso vinculado</strong>
                  <span>{sidebar?.withoutCourse ?? 0} {sidebar?.withoutCourse === 1 ? 'artigo' : 'artigos'}</span>
                </span>
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </div>
          </aside>
        </div>
      </div>
    </GlobalShell>
  );
}

function Featured({ article, onOpen }) {
  const tag = article.tags?.[0];
  return (
    <div className="arts-featured" role="link" tabIndex={0} onClick={() => onOpen(article)} onKeyDown={(e) => e.key === 'Enter' && onOpen(article)}>
      {article.coverUrl
        ? <ArticleCover article={article} className="arts-featured__cover" />
        : <span className="arts-featured__cover arts-featured__cover--empty" aria-hidden="true"><Newspaper size={28} strokeWidth={1.6} /></span>}
      <div className="arts-featured__body">
        <div className="arts-featured__badges">
          <span className="arts-featured__badge">Destaque</span>
          {tag && <span className="arts-featured__tag"><Hash size={11} strokeWidth={2.6} aria-hidden="true" />{tag.name}</span>}
        </div>
        <h2>{article.title}</h2>
        {article.subtitle && <p>{article.subtitle}</p>}
        <div className="arts-featured__foot">
          <span className="arts-featured__avatar">{initials(article.author?.name)}</span>
          <span><strong>{article.author?.name}</strong> · {shortDate(article.publishedAt)} · {article.readingMinutes} min</span>
          <span className="arts-featured__cta">Ler artigo <ArrowRight size={15} aria-hidden="true" /></span>
        </div>
      </div>
    </div>
  );
}

export default Articles;
